import React, { useState } from "react"
import { useStaticQuery, graphql, Link } from "gatsby"

interface InfoFrontmatter {
  listName: string
  title: string
  slug: string
  added?: string
}

interface ProjectFrontmatter extends InfoFrontmatter {
  nameOfClass?: string
  popupGithubLink?: string
  category?: string
}

interface MarkdownEdge<T> {
  node: { frontmatter: T }
}

interface ItemsListData {
  projects: { edges: MarkdownEdge<ProjectFrontmatter>[] }
  info: { edges: MarkdownEdge<InfoFrontmatter>[] }
}

const CATEGORY_ORDER: string[] = [
  "Backend & APIs",
  "Cloud & DevOps",
  "Security",
  "Realtime & Streaming",
  "Data & AI",
  "Reference & Tools",
]

const repoHandle = (url?: string): string => {
  if (!url) return ""
  return url.replace(/^https?:\/\/(www\.)?github\.com\//, "")
}

const ItemsList: React.FC = () => {
  const data = useStaticQuery<ItemsListData>(graphql`
    query {
      projects: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "//projects/[^/]+$/" } }
        sort: {
          fields: [frontmatter___added, frontmatter___title]
          order: DESC
        }
      ) {
        edges {
          node {
            frontmatter {
              listName
              nameOfClass
              popupGithubLink
              title
              slug
              added
              category
            }
          }
        }
      }
      info: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "//info/[^/]+$/" } }
        sort: {
          fields: [frontmatter___added, frontmatter___title]
          order: ASC
        }
      ) {
        edges {
          node {
            frontmatter {
              listName
              title
              slug
              added
            }
          }
        }
      }
    }
  `)

  const info = data.info.edges.map(item => (
    <li key={item.node.frontmatter.title} className="infoItem">
      <Link
        className="popupWindowLinkButton"
        style={{ cursor: "pointer" }}
        to={item.node.frontmatter.slug}
      >
        {item.node.frontmatter.listName}
      </Link>
    </li>
  ))

  const contactItem = (
    <li key="contact" className="infoItem">
      <Link
        className="popupWindowLinkButton"
        style={{ cursor: "pointer" }}
        to="/contact"
      >
        Contact
      </Link>
    </li>
  )

  const grouped = new Map<string, ProjectFrontmatter[]>()
  for (const edge of data.projects.edges) {
    const cat = edge.node.frontmatter.category || "Reference & Tools"
    if (!grouped.has(cat)) grouped.set(cat, [])
    grouped.get(cat)!.push(edge.node.frontmatter)
  }

  const orderedCategories = [
    ...CATEGORY_ORDER.filter(c => grouped.has(c)),
    ...Array.from(grouped.keys()).filter(c => !CATEGORY_ORDER.includes(c)),
  ]

  const [collapsed, setCollapsed] = useState<Set<string>>(
    () => new Set(orderedCategories)
  )
  const toggleCategory = (cat: string) => {
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(cat)) next.delete(cat)
      else next.add(cat)
      return next
    })
  }

  const renderProjectRow = (fm: ProjectFrontmatter) => {
    const repo = repoHandle(fm.popupGithubLink)
    return (
      <li
        key={fm.title}
        className={fm.nameOfClass}
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Link
          className="popupWindowLinkButton"
          style={{ cursor: "pointer", flex: "1 1 auto", minWidth: 0 }}
          to={fm.slug}
        >
          {fm.listName}
        </Link>
        <span
          style={{
            paddingLeft: "1rem",
            flex: "0 0 auto",
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          <span style={{ marginRight: repo ? "0.75rem" : 0 }}>{fm.added}</span>
          {repo && fm.popupGithubLink ? (
            <a
              href={fm.popupGithubLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
            >
              {repo}
            </a>
          ) : null}
        </span>
      </li>
    )
  }

  return (
    <ul className="mappedItemsList">
      <li>Info</li>
      {info}
      {contactItem}
      <li>Projects</li>
      {orderedCategories.map(cat => {
        const isCollapsed = collapsed.has(cat)
        const items = grouped.get(cat)!
        return (
          <React.Fragment key={cat}>
            <li
              className={`projectCategory${isCollapsed ? " collapsed" : ""}`}
              onClick={() => toggleCategory(cat)}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  toggleCategory(cat)
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={!isCollapsed}
            >
              <span className="projectCategoryName">{cat}</span>
              <span className="projectCategoryCount">{items.length}</span>
            </li>
            {!isCollapsed && items.map(renderProjectRow)}
          </React.Fragment>
        )
      })}
    </ul>
  )
}

export default ItemsList
